import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiCaptchaComponent } from '@fi-app/utilities/captcha/fi-captcha.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AccessRightFilterCollectionModel, ILightWeightBusinessUnit, ILightWeightObject } from '@fi-shared/models';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';
import { IUserModel } from '@fi-shared/models/user.model';
import { IContactModel } from '@fi-shared/models/common';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { FiContactsService, FiUserContactDataItemType } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IFiContactData } from '@fi-shared/fi-contacts/fi-contacts';
import { FiUserDataService, FiUserSignInType } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { takeUntil } from 'rxjs/operators';
import { NgModel } from '@angular/forms';
import { FiUserSignInSettings } from '@fi-app/user/user-edit/fi-user-sign-in-settings';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppTextService } from '@fi-shared/app-text';
import { UserModel } from '@fi-app/user/user.model';
import { FiUserHelperService } from '@fi-shared/fi-user-shared/fi-user-helper.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { UserAccessRequestsService } from '@fi-app/user/user-access-requests.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { cloneDeep } from '@fi-shared/utils';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { IContentLanguage } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { UserService } from '@fi-app/user/user.service';

@Component({
    selector: 'fi-request-access',
    templateUrl: './request-access.component.html',
    styleUrls: ['./request-access.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-request-access' },
    providers: [],
    standalone: false,
})
export class RequestAccessComponent implements OnInit, OnDestroy {
    @Input('user') User: UserModel;
    @Input() accessRightFilterCollection: AccessRightFilterCollectionModel;
    @Input() applicationLanguages: IContentLanguage[];
    @Input() contentLanguages: IContentLanguage[];

    @ViewChild('PasswordModel', { static: false }) PasswordModel: NgModel;
    @ViewChild(FiCaptchaComponent, { static: false }) captcha: FiCaptchaComponent;

    isRoleDisabledFn: FiDropdownItemDisabledFn<ILightWeightObject> = (
        item: ILightWeightObject,
        allItems: ILightWeightObject[],
    ) => {
        const availableRoleIds: number[] = this.accessRightFilterCollection.AccessRightCoreFilter.RoleIds;
        return !availableRoleIds?.length || availableRoleIds.indexOf(item.Id) === -1;
    };

    EditTabs: EditTabsModel;
    isPhoneNumberEditDisabled: boolean;
    FiCustomValidatorPatterns = FiCustomValidatorPatterns;
    emailContactData: IFiContactData;
    phoneContactData: IFiContactData;
    HasEditAccess = true;
    lightWeightBusinessAreas$ = new BehaviorSubject<ILightWeightObject[]>([]);
    lightWeightRoles: ILightWeightObject[];
    fiUserSignInSettings: FiUserSignInSettings;

    constructor(
        private injector: Injector,
        private appTextService: AppTextService,
        private userHelperService: FiUserHelperService,
        private fiLoadingService: LoadingService,
        private userAccessRequestsService: UserAccessRequestsService,
        private fiDialogsService: FiDialogsService,
        private fiContactsService: FiContactsService,
        private saveChangesWarningService: SaveChangesWarningService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private contentLanguagesService: ContentLanguagesService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
        private userService: UserService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.userEditInit();
        this.setPageHeaderData();
        this.emailContactData = this.getContactData(FiUserContactDataItemType.Emails);
        this.phoneContactData = this.getContactData(FiUserContactDataItemType.PhoneNumbers);
        this.fiUserSignInSettings.updateSignInInputValidity$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((signInType: FiUserSignInType) => {
                if (signInType === FiUserSignInType.PhoneNumber) {
                    this.onPhoneContactChanged(this.fiUserSignInSettings.signInContact);
                } else if (signInType === FiUserSignInType.Email) {
                    this.onEmailContactChanged(this.fiUserSignInSettings.signInContact);
                }
            });
    }

    protected isOptionHiddenFn(item: ILightWeightBusinessUnit): boolean {
        return !item.IsActivated;
    }

    private userEditInit() {
        this._initLightWeightRoles();
        this._initLanguages();
        this.HasEditAccess = true;
        this.User.checkAddDefaults(this.userDataService.applicationLanguage);

        this.saveChangesWarningService.start((isReceivingInitialObject) => {
            const obj: UserModel = cloneDeep(this.User);
            if (isReceivingInitialObject) obj.ApplicationLanguageType = this.User.ApplicationLanguageTypeOrig;

            return obj;
        });

        this.lightWeightBusinessAreas$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((businessAreas) => {
            this.User.UserToBusinessAreaIds = businessAreas.map((businessArea) => businessArea.Id);
        });
        this.fiUserSignInSettings = new FiUserSignInSettings(this.User, this.injector);
    }

    private _initLanguages() {
        if (!this.User.ApplicationLanguageType)
            this.User.ApplicationLanguageType = this.customerDataService.customerData.DefaultApplicationLanguageType;

        if (!this.User.PreferredContentLanguageId)
            this.User.PreferredContentLanguageId = this.contentLanguageSharedService.getDefaultContentLanguageId();
    }

    private _initLightWeightRoles() {
        this.lightWeightRoles = this.accessRightFilterCollection.AccessRightCoreFilter.Roles.slice();
        this.User.Roles.forEach((role) => {
            // if user role is not already in the list then add it
            if (this.accessRightFilterCollection.AccessRightCoreFilter.RoleIds.indexOf(role.Id) === -1)
                this.lightWeightRoles.push(role);
        });
    }

    getDataForUniqueEmailCheckFn(): (contact: IContactModel) => IFiUniqueContactData {
        return this.userHelperService.getDataForUniqueEmailCheckFn(
            this.User.Id,
            this.User.IsSystemObject,
            this.User.EmailAddressContacts,
        );
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(
            this.appTextService.get('other_settings.request_access'),
            this.appTextService.get('back'),
            this.appTextService.get('request_access.send_request'),
        );
        this.EditTabs.Tabs.push(new EditTabModel('', ''));
    }

    onPreferredBusinessUnitIdChange() {
        this.User.UserToBusinessUnitIds = [this.User.PreferredBusinessUnitId];
        // this.updateBusinessAreas();
    }

    // update dropdowns in user edit and request access component
    protected processUpdateApplicationLanguage(newApplicationLanguageType: FiApplicationLanguageType) {
        this.userService.updateApplicationLanguage(newApplicationLanguageType).then(() => {
            //this.pe.profileItemOnSave();
            this.User.ApplicationLanguageType = newApplicationLanguageType;
            this.contentLanguagesService.UpdateContentLanguageNames(this.applicationLanguages);

            const applicationLanguages = this.contentLanguagesService.GetApplicationLanguages();
            this.contentLanguages.forEach((item) => {
                const newApplicationLanguage = applicationLanguages.find((lang) => lang.Id === item.Id);
                if (newApplicationLanguage) item.Name = newApplicationLanguage.Name;
            });
            const phoneNumberContacts = cloneDeep(this.User.PhoneNumberContacts);
            this.User.PhoneNumberContacts.length = 0;
            phoneNumberContacts.forEach((contact) => {
                this.User.PhoneNumberContacts.push(contact);
            });
        });
    }

    Save() {
        //this.pe.profileItemOnSave();
        this.removeUidFromContacts();
        this.fiLoadingService
            .trackPromise(this.userAccessRequestsService.saveAccessRequest(this.User))
            .then((data: IUserModel) => {
                if (data) {
                    this.showSaveSuccessDialog(data.MediaBankPhysicalFileName);
                } else {
                    this.showSaveErrorDialog();
                }
            })
            .catch(() => this.captcha.reset());
    }

    private removeUidFromContacts() {
        this.User.EmailAddressContacts = this.fiContactsService.removeUidFromContacts(this.User.EmailAddressContacts);
        this.User.PhoneNumberContacts = this.fiContactsService.removeUidFromContacts(this.User.PhoneNumberContacts);
    }

    private showSaveErrorDialog() {
        this.fiDialogsService.error({
            messageText: this.appTextService.get('error_message.default_header'),
        });
    }

    private showSaveSuccessDialog(mediaBankPhysicalFileName: string) {
        this.User.MediaBankPhysicalFileName = mediaBankPhysicalFileName;
        this.saveChangesWarningService.stop();

        this.fiDialogsService
            .notify({
                headerText: this.appTextService.get('thanks'),
                messageText: this.appTextService.get('request_sent'),
            })
            .afterClosed(() => this.stateService.go(FiStateNames.Login.Auth));
    }

    onEmailContactChanged($event: IContactModel) {
        this.emailContactData.onContactValueChanged($event);
    }

    onPhoneContactChanged($event: IContactModel) {
        this.phoneContactData.onContactValueChanged($event);
    }

    private getContactData(userContactDataItemType: FiUserContactDataItemType) {
        return this.userHelperService.getUserContactData({
            user: this.User,
            userContactDataItemType: userContactDataItemType,
            contactUniqueDataFn: () => [],
            isPKUser: false,
            onDestroy$: this.ngOnDestroy$,
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
